<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <mini-statistics-card
          title="商户总数"
          :value="String(dashboardData.total_merchants)"
          :percentage="{
            value: '平台入驻商户',
            color: 'text-success',
          }"
          :icon="{
            component: 'ni ni-shop',
            background: iconBackground,
          }"
          direction-reverse
        />
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
        <mini-statistics-card
          title="设备总数"
          :value="String(dashboardData.total_devices)"
          :percentage="{
            value: '在线 ' + dashboardData.online_devices + ' / 离线 ' + dashboardData.offline_devices,
            color: dashboardData.online_devices > 0 ? 'text-success' : 'text-danger',
          }"
          :icon="{
            component: ' ni ni-sound-wave',
            background: iconBackground,
          }"
          direction-reverse
        />
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4" v-if="isSpecialAccount">
        <mini-statistics-card
          title="待审核进件"
          :value="String(dashboardData.pending_submission)"
          :percentage="{
            value: '待处理的商户进件申请',
            color: dashboardData.pending_submission > 0 ? 'text-warning' : 'text-success',
          }"
          :icon="{
            component: 'ni ni-single-copy-04',
            background: 'bg-gradient-warning',
          }"
          direction-reverse
        />
      </div>
      <div class="col-xl-3 col-sm-6 mb-xl-0" :class="isSpecialAccount ? '' : 'ms-auto'">
        <mini-statistics-card
          title="今日销售额"
          :value="'¥' + formatPrice(dashboardData.today_sales)"
          :percentage="{
            value: '今日实时交易总额',
            color: 'text-success',
          }"
          :icon="{
            component: 'ni ni-cart',
            background: 'bg-gradient-info',
          }"
          direction-reverse
        />
      </div>
    </div>
    <div class="mt-4 row">
      <div class="mb-4 col-lg-5 mb-lg-0">
        <div class="card z-index-2">
          <div class="p-3 card-body">
            <h6 class="mb-3">设备在线状态</h6>
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder">状态</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder">数量</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span class="badge bg-success">在线</span></td>
                    <td class="font-weight-bold">{{ dashboardData.online_devices }}</td>
                  </tr>
                  <tr>
                    <td><span class="badge bg-danger">离线</span></td>
                    <td class="font-weight-bold">{{ dashboardData.offline_devices }}</td>
                  </tr>
                  <tr>
                    <td><strong>合计</strong></td>
                    <td class="font-weight-bold">{{ dashboardData.total_devices }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-7">
        <div class="card z-index-2">
          <div class="p-3 card-body">
            <h6 class="mb-3">快速入口</h6>
            <div class="row">
              <div class="col-md-4 mb-3">
                <div class="card bg-gradient-primary text-white cursor-pointer" @click="$router.push('/mechants')">
                  <div class="card-body p-3 text-center">
                    <i class="ni ni-shop mb-2" style="font-size: 2rem;"></i>
                    <h6 class="text-white mb-0">商户管理</h6>
                  </div>
                </div>
              </div>
              <div class="col-md-4 mb-3">
                <div class="card bg-gradient-info text-white cursor-pointer" @click="$router.push('/devices')">
                  <div class="card-body p-3 text-center">
                    <i class="ni ni-sound-wave mb-2" style="font-size: 2rem;"></i>
                    <h6 class="text-white mb-0">设备管理</h6>
                  </div>
                </div>
              </div>
              <div class="col-md-4 mb-3" v-if="isSpecialAccount">
                <div class="card bg-gradient-warning text-white cursor-pointer" @click="$router.push('/submission')">
                  <div class="card-body p-3 text-center">
                    <i class="ni ni-single-copy-04 mb-2" style="font-size: 2rem;"></i>
                    <h6 class="text-white mb-0">进件审核</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row my-4">
      <div class="col-lg-12">
        <gradient-line-chart
          id="sales-trend-chart"
          title="交易趋势"
          :description="trendDescription"
          :chart="trendChart"
        />
      </div>
    </div>
    <div class="row">
      <!-- 最新订单 -->
      <div class="col-lg-5 mb-4">
        <div class="card h-100">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h6>最新订单</h6>
              <p class="text-xs text-secondary">全平台最近 10 条订单</p>
            </div>
            <button class="btn btn-sm btn-outline-info" @click="fetchNewestOrders">
              <i class="ni ni-refresh"></i> 刷新
            </button>
          </div>
          <div class="card-body p-3">
            <div v-if="orderLoading" class="text-center text-secondary text-xs py-4">加载中...</div>
            <div v-else-if="newestOrders.length > 0" class="table-responsive">
              <table class="table table-sm align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-xs text-secondary" style="width:40px">#</th>
                    <th class="text-xs text-secondary">订单号</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(order, idx) in newestOrders.slice(0, 10)" :key="idx">
                    <td class="text-xs text-secondary">{{ idx + 1 }}</td>
                    <td class="text-xs font-monospace">{{ order }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-secondary text-xs py-4">暂无订单</div>
          </div>
        </div>
      </div>
      <!-- 今日收入明细 -->
      <div class="col-lg-7 mb-4">
        <div class="card h-100">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h6>今日收入明细</h6>
              <p class="text-xs text-secondary">按设备维度展示今日收入</p>
            </div>
            <div class="d-flex align-items-center gap-2">
              <select v-model="todaySortField" @change="sortTodayItems" class="form-select form-select-sm" style="width:120px">
                <option value="sum_fee">按金额排序</option>
                <option value="net_fee">按净收排序</option>
                <option value="sum_coins">按出币排序</option>
              </select>
              <button class="btn btn-sm btn-outline-info" @click="fetchTodayIncomes">
                <i class="ni ni-refresh"></i> 刷新
              </button>
            </div>
          </div>
          <div class="card-body p-3">
            <div v-if="todayLoading" class="text-center text-secondary text-xs py-4">加载中...</div>
            <div v-else-if="todayItems.length > 0" class="table-responsive">
              <table class="table table-sm align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-xs text-secondary">设备ID</th>
                    <th class="text-xs text-secondary text-end">总金额</th>
                    <th class="text-xs text-secondary text-end">净收入</th>
                    <th class="text-xs text-secondary text-end">出币数</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in todayItems" :key="item.board_id">
                    <td class="text-xs font-monospace">{{ item.board_id }}</td>
                    <td class="text-xs text-end font-weight-bold">¥{{ formatPrice(item.sum_fee) }}</td>
                    <td class="text-xs text-end text-success">¥{{ formatPrice(item.net_fee) }}</td>
                    <td class="text-xs text-end">{{ (item.sum_coins / 100).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-secondary text-xs py-4">今日暂无交易数据</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import GradientLineChart from "@/examples/Charts/GradientLineChart.vue";
import { getDashboardData, getSalesTrend, getNewestOrder, getTodayIncomes } from "@/api/dashboard"
import { getToken } from '@/utils/auth'

export default {
  name: "dashboard-default",
  data() {
    return {
      iconBackground: "bg-gradient-success",
      dashboardData: {
        total_merchants: 0,
        total_devices: 0,
        online_devices: 0,
        offline_devices: 0,
        pending_submission: 0,
        today_sales: 0,
      },
      trendData: [],
      trendDays: 7,
      newestOrders: [],
      orderLoading: false,
      todayItems: [],
      todayLoading: false,
      todaySortField: 'sum_fee',
    };
  },
  components: {
    MiniStatisticsCard,
    GradientLineChart,
  },
  computed: {
    isSpecialAccount() {
      const token = getToken();
      return token && token.is_special === 1;
    },
    trendDescription() {
      return '近 ' + this.trendDays + ' 天每日交易额趋势';
    },
    trendChart() {
      if (!this.trendData || this.trendData.length === 0) {
        return { labels: [], datasets: [{ label: '交易额', data: [] }, { label: '订单数', data: [] }] };
      }
      return {
        labels: this.trendData.map(item => item.date),
        datasets: [
          {
            label: '交易额 (分)',
            data: this.trendData.map(item => item.fee),
          },
          {
            label: '交易笔数',
            data: this.trendData.map(item => item.coins),
          },
        ],
      };
    },
  },
  mounted() {
    this.fetchDashboardData();
    this.fetchSalesTrend();
    this.fetchNewestOrders();
    this.fetchTodayIncomes();
  },
  methods: {
    async fetchDashboardData() {
      try {
        const resp = await getDashboardData();
        if (resp.code === 0) {
          this.dashboardData = resp.desc;
        }
      } catch (err) {
        console.error("获取仪表盘数据失败:", err);
      }
    },
    async fetchSalesTrend() {
      try {
        const resp = await getSalesTrend({ days: this.trendDays });
        if (resp.code === 0 && Array.isArray(resp.desc)) {
          this.trendData = resp.desc;
        }
      } catch (err) {
        console.error("获取交易趋势失败:", err);
      }
    },
    toggleTrendDays(days) {
      this.trendDays = days;
      this.fetchSalesTrend();
    },
    formatPrice(price) {
      // fee 以分为单位存储，除以 100 转换为元，保留两位小数
      if (!price && price !== 0) return '0';
      return (Number(price) / 100).toFixed(2);
    },
    async fetchNewestOrders() {
      this.orderLoading = true;
      try {
        const resp = await getNewestOrder({});
        if (resp.code === 0) {
          if (Array.isArray(resp.desc)) {
            this.newestOrders = resp.desc;
          } else if (typeof resp.desc === 'string') {
            this.newestOrders = [resp.desc];
          }
        }
      } catch (err) {
        console.error('获取最新订单失败:', err);
      } finally {
        this.orderLoading = false;
      }
    },
    async fetchTodayIncomes() {
      this.todayLoading = true;
      try {
        const resp = await getTodayIncomes({});
        if (resp.code === 0 && Array.isArray(resp.desc)) {
          this.todayItems = resp.desc;
          this.sortTodayItems();
        }
      } catch (err) {
        console.error('获取今日收入失败:', err);
      } finally {
        this.todayLoading = false;
      }
    },
    sortTodayItems() {
      const field = this.todaySortField;
      this.todayItems.sort((a, b) => (b[field] || 0) - (a[field] || 0));
    },
  },
};
</script>
<style scoped>
.cursor-pointer { cursor: pointer; }
.cursor-pointer:hover { opacity: 0.9; }
.badge { padding: 0.35em 0.65em; font-size: 0.85em; }
</style>
